import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/apiClient";
import type { ProfileSearchResponse } from "@/types/settings";

export interface ProfileObj {
  fullName: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface ProfileLookupProps {
  id?: string;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  /** Debounce delay in ms before firing the search API (mirrors Blazor DebounceDelay=1000). */
  debounceDelay?: number;
  onSelect: (profile: ProfileObj | null) => void;
}

/**
 * Auto-complete profile lookup. Mirrors Blazor ProfileLookup component.
 * - Debounces search input (default 500 ms)
 * - Fires onSelect(ProfileObj) when user picks an item
 * - Fires onSelect(null) when the field is cleared
 */
export function ProfileLookup({ id, value, disabled = false, placeholder = "--", debounceDelay = 500, onSelect }: ProfileLookupProps) {
  const [inputValue, setInputValue] = useState(value ?? "");
  const [open, setOpen] = useState(false);
  // debouncedTerm drives the API query; only updated after the delay
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track whether the current inputValue came from a dropdown selection
  const selectedRef = useRef(false);

  // Sync external value changes (e.g. form reset)
  useEffect(() => {
    selectedRef.current = true; // treat external updates as "selected"
    setInputValue(value ?? "");
    if (!value) setDebouncedTerm("");
  }, [value]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["profile-search", debouncedTerm],
    queryFn: () =>
      // Mirrors Blazor: _DraftCalenderService.searchDeloitteProfile(userInput)
      // endpoint: ProfileSearch/searchDeloitteProfile?searchtext={text}
      apiGet<ProfileSearchResponse[]>(
        `ProfileSearch/searchDeloitteProfile?searchtext=${encodeURIComponent(debouncedTerm)}`
      ),
    enabled: debouncedTerm.trim().length >= 2,
    staleTime: 30_000
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    selectedRef.current = false;
    setInputValue(val);

    // Clear immediately; debounce the search
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!val) {
      setDebouncedTerm("");
      setOpen(false);
      onSelect(null);
      return;
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedTerm(val);
      setOpen(true);
    }, debounceDelay);
  }

  function handleSelect(profile: ProfileSearchResponse) {
    const fullName = profile.fullName || `${profile.firstName} ${profile.lastName}`.trim();
    selectedRef.current = true;
    setInputValue(fullName);
    setOpen(false);
    setDebouncedTerm("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSelect({ fullName, email: profile.email, firstName: profile.firstName, lastName: profile.lastName });
  }

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={handleChange}
        disabled={disabled}
        readOnly={disabled}
        placeholder={disabled ? "" : placeholder}
        autoComplete="off"
        className={`Add-PlaceHolder${disabled ? " div-disabled" : ""}`}
        style={{ width: "100%" }}
        onFocus={() => { if (debouncedTerm.trim().length >= 2) setOpen(true); }}
      />
      {open && (isFetching || results.length > 0) && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            background: "#fff",
            border: "1px solid #D0D0CE",
            maxHeight: 200,
            overflowY: "auto",
            margin: 0,
            padding: 0,
            listStyle: "none"
          }}
        >
          {isFetching && results.length === 0 && (
            <li style={{ padding: "8px 10px", fontSize: 12, color: "#63666A" }}>Searching&hellip;</li>
          )}
          {!isFetching && results.length === 0 && debouncedTerm.length >= 2 && (
            <li style={{ padding: "8px 10px", fontSize: 12, color: "#63666A" }}>No results found.</li>
          )}
          {results.map((p) => {
            const display = p.fullName || `${p.firstName} ${p.lastName}`.trim();
            return (
              <li
                key={p.email}
                role="option"
                aria-selected={false}
                onMouseDown={() => handleSelect(p)}
                className="row mb-3"
                style={{ padding: "6px 10px", cursor: "pointer", fontSize: 13, color: "#000", listStyle: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#E8E8E8")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                <span style={{ marginLeft: 1, display: "block", fontWeight: 400 }}>{display}</span>
                <small style={{ color: "#63666A" }}>{p.email}</small>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
